import React, { useCallback, useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { usePropertyConfiguration } from "../../../components/property-configuration-provider";
import { useConfig } from "../../../lib/config";
import { useHttpClient } from "../../../lib/http";
import { Reservation, RoomReservation } from "../../modification/model";
import { TYPE } from "../../taxation";
import { Api } from "../apis";
import { PropertyInfoData } from "../index";
import {
  AmountRender,
  CountryRender,
  DailyPricesRender,
  DateRender,
  DiscountRender,
  ExchangeTypeRender,
  ExtraRender,
  InventoryRender,
  RoomReservationStatusRender,
} from "../renders";
import { StyledCard, StyledGoBackButton } from "../styles";
import { CombinedBookingModal } from "./CombinedBookingModal";
import { CreditCardDataTooltip } from "./CreditCardDataTooltip";
import { DiscountModal } from "./DiscountModal";

const createMessage = <T extends Record<string, Record<string, MessageDescriptor>>>(messages: T) =>
  messages;

const messages = createMessage({
  taxesText: {
    TAXES_INCLUDED: {
      id: "propertyConfiguration.taxesText.taxesIncluded",
      defaultMessage: "Taxes included",
    },
    TAXES_EXCLUDED: {
      id: "propertyConfiguration.taxesText.taxesExcluded",
      defaultMessage: "Taxes excluded",
    },
    TAXES_AND_FEES_INCLUDED: {
      id: "propertyConfiguration.taxesText.taxesAndFeesIncluded",
      defaultMessage: "Taxes and fees included",
    },
    TAXES_AND_FEES_EXCLUDED: {
      id: "propertyConfiguration.taxesText.taxesAndFeesExcluded",
      defaultMessage: "Taxes and fees excluded",
    },
    TAXES_INCLUDED_FEES_EXCLUDED: {
      id: "propertyConfiguration.taxesText.taxesIncludedFeesExcluded",
      defaultMessage: "Taxes included / fees excluded",
    },
  },
  reservation: {
    title: {
      id: "reservationDetail.reservationData.title",
      defaultMessage: "Reservation Data",
    },
    id: {
      id: "reservationDetail.reservationData.id",
      defaultMessage: "Reservation",
    },
    status: {
      id: "reservationDetail.reservationData.status",
      defaultMessage: "Status",
    },
    reference: {
      id: "reservationDetail.reservationData.reference",
      defaultMessage: "Reference",
    },
    agent: {
      id: "reservationDetail.reservationData.agent",
      defaultMessage: "AGT",
    },
    placementDate: {
      id: "reservationDetail.reservationData.placementDate",
      defaultMessage: "Date",
    },
  },
  customer: {
    title: {
      id: "reservationDetail.customer.title",
      defaultMessage: "Customer Data",
    },
    name: {
      id: "reservationDetail.customer.name",
      defaultMessage: "Name",
    },
    email: {
      id: "reservationDetail.customer.email",
      defaultMessage: "Email",
    },
    country: {
      id: "reservationDetail.customer.country",
      defaultMessage: "Country",
    },
    phone: {
      id: "reservationDetail.customer.phone",
      defaultMessage: "Phone",
    },
    comments: {
      id: "reservationDetail.customer.comments",
      defaultMessage: "Comments",
    },
  },
  dates: {
    title: {
      id: "reservationDetail.dates.title",
      defaultMessage: "Dates",
    },
    checkIn: {
      id: "reservationDetail.dates.checkIn",
      defaultMessage: "Check-In",
    },
    checkOut: {
      id: "reservationDetail.dates.checkOut",
      defaultMessage: "Check-Out",
    },
    nights: {
      id: "reservationDetail.dates.nights",
      defaultMessage: "Night(s)",
    },
  },
  accommodation: {
    title: {
      id: "reservationDetail.accommodation.title",
      defaultMessage: "Accommodation",
    },
    type: {
      id: "reservationDetail.accommodation.type",
      defaultMessage: "Type",
    },
    totalPerPerson: {
      id: "reservationDetail.accommodation.totalPerPerson",
      defaultMessage: "Total (persons)",
    },
    pricePerNight: {
      id: "reservationDetail.accommodation.pricePerNight",
      defaultMessage: "Price per night (EUR)",
    },
    total: {
      id: "reservationDetail.accommodation.total",
      defaultMessage: `Accommodation Total ({currency})`,
    },
    combinedRate: {
      id: "reservationDetail.combinedRate",
      defaultMessage: "Combined rate",
    },
  },
  discounts: {
    title: {
      id: "reservationDetail.discounts.title",
      defaultMessage: "Discounts",
    },
    seeAppliedDiscounts: {
      id: "reservationDetail.discounts.seeAppliedDiscounts",
      defaultMessage: "See applied discounts",
    },
  },
  extras: {
    title: {
      id: "reservationDetail.extras.title",
      defaultMessage: "Extras",
    },
  },
  total: {
    title: {
      id: "reservationDetail.total.title",
      defaultMessage: "Total",
    },
    prePaid: {
      id: "reservationDetail.total.prePaid",
      defaultMessage: "Pre paid",
    },
    pending: {
      id: "reservationDetail.total.pending",
      defaultMessage: "Pending",
    },
    dccExchangeRateType: {
      id: "reservationDetail.total.dccExchangeRateType",
      defaultMessage: "Exchange rate",
    },
    dccMarkup: {
      id: "reservationDetail.total.dccMarkup",
      defaultMessage: "Including bank commission",
    },
  },
  actions: {
    edit: {
      id: "reservationDetail.actions.edit",
      defaultMessage: "Edit",
    },
    print: {
      id: "reservationDetail.actions.print",
      defaultMessage: "Print",
    },
    showCreditCardData: {
      id: "reservationDetail.actions.showCreditCardData",
      defaultMessage: "Show TC data",
    },
    viewConditions: {
      id: "reservationDetail.actions.viewConditions",
      defaultMessage: "View conditions",
    },
    back: {
      id: "default.goBack",
      defaultMessage: "Go Back",
    },
  },
  errors: {
    roomReservationNotFound: {
      id: "reservationDetail.roomReservationNotFound",
      defaultMessage: "Room reservation not found",
    },
  },
  utility: {
    arrivalTime: {
      id: "default.arrivalTime",
      defaultMessage: "Arrival time",
    },
  },
});

export interface DetailViewProps {
  propertyData: PropertyInfoData;
  roomStayId: string;
  reservation: Reservation;
  showEditButton: boolean;
  showCCButton: boolean;
  onShowCreditCardData?: () => void;
}

export function DetailView({
  propertyData,
  roomStayId,
  reservation,
  showEditButton,
  showCCButton,
  onShowCreditCardData,
}: DetailViewProps) {
  const config = useConfig();
  const history = useHistory();
  const propertyConfiguration = usePropertyConfiguration();
  const apiClient = useHttpClient(config.apiEndpoint);
  const intl = useIntl();

  const api = useMemo(() => new Api(apiClient, reservation.hotelTicker), [
    apiClient,
    reservation.hotelTicker,
  ]);

  const roomReservation: RoomReservation = useMemo(() => {
    const value = reservation.roomReservations.find(it => it.confirmationId === roomStayId);
    if (!value) {
      throw new Error(messages.errors.roomReservationNotFound.defaultMessage);
    }

    if (value.additionalRequests?.arrivalTime) {
      value.comments = `${value.comments} \n ${intl.formatMessage(messages.utility.arrivalTime)}: ${
        value.additionalRequests.arrivalTime
      }`;
    }

    return value;
  }, [intl, reservation.roomReservations, roomStayId]);

  const pendingAmount = useMemo(() => {
    const feesTotalAmount = Object.values(roomReservation.taxations)
      .filter(it => it.type === TYPE.FEE)
      .reduce((acc, fee) => acc + fee.totalAmount, 0);
    return (
      Number(roomReservation.totalAmount.amount) -
      roomReservation.guaranteeAmount.amount +
      feesTotalAmount
    );
  }, [
    roomReservation.guaranteeAmount.amount,
    roomReservation.taxations,
    roomReservation.totalAmount.amount,
  ]);

  const totalMessage = useMemo(() => {
    switch (propertyConfiguration?.taxesText) {
      case "TAXES_EXCLUDED":
        return messages.taxesText.TAXES_EXCLUDED;
      case "TAXES_AND_FEES_INCLUDED":
        return messages.taxesText.TAXES_AND_FEES_INCLUDED;
      case "TAXES_AND_FEES_EXCLUDED":
        return messages.taxesText.TAXES_AND_FEES_EXCLUDED;
      case "TAXES_INCLUDED_FEES_EXCLUDED":
        return messages.taxesText.TAXES_INCLUDED_FEES_EXCLUDED;
      case "TAXES_INCLUDED":
      default:
        return messages.taxesText.TAXES_INCLUDED;
    }
  }, [propertyConfiguration]);

  const handleSeeConditions = useCallback(async () => {
    const token = await api.getReservationToken(reservation.id);
    window.open(
      `${config.engineApiEndpoint}/hotel/${reservation.hotelTicker}/confirmation?token=${token}`,
      "_blank"
    );
  }, [api, config.engineApiEndpoint, reservation.hotelTicker, reservation.id]);

  const handleModification = useCallback(() => {
    history.push(`/${reservation.hotelTicker}/reservationModification/${roomStayId}`);
  }, [history, reservation, roomStayId]);

  const [isOpenCombinedBookingModal, setIsOpenCombinedBookingModal] = useState<boolean>(false);
  const [isOpenDiscountModal, setIsOpenDiscountModal] = useState<boolean>(false);

  const handleToggleCombinedBookingModal = useCallback(() => {
    setIsOpenCombinedBookingModal(prevState => !prevState);
  }, []);

  const handleToggleDiscountModal = useCallback(() => {
    setIsOpenDiscountModal(prevState => !prevState);
  }, []);

  return (
    <StyledCard className="ml-4 mr-4 mb-4 mt-4 fs-3">
      {roomReservation?.bookingOption ? (
        <CombinedBookingModal
          isOpen={isOpenCombinedBookingModal}
          bookingOption={roomReservation.bookingOption}
          onClose={handleToggleCombinedBookingModal}
        />
      ) : null}
      <DiscountModal
        isOpen={isOpenDiscountModal}
        dailyDiscounts={roomReservation.dailyDiscounts}
        dailyValues={roomReservation.dailyValues}
        onClose={handleToggleDiscountModal}
      />
      <Card.Body>
        <div>
          <div className="border-bottom pb-2 mb-3 text-[21px]">
            <FormattedMessage {...messages.reservation.title} />
          </div>
          <div className="mb-3">
            <div>{propertyData.name}</div>
            <div>{propertyData.contact?.address}</div>
            <div>{propertyData.contact?.phone}</div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.reservation.id} />
            </div>
            <div>{roomStayId}</div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.reservation.status} />
            </div>
            <div>
              <RoomReservationStatusRender status={roomReservation.status} />
            </div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.reservation.reference} />
            </div>
            <div>{reservation.id}</div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.reservation.agent} />
            </div>
            <div>{reservation.channelAddress || "-"}</div>
          </div>
          <div className="mb-5">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.reservation.placementDate} />
            </div>
            <div>
              <DateRender date={reservation.placementDate} />
            </div>
          </div>
        </div>
        <div>
          <div className="border-bottom pb-2 mb-3 text-[21px]">
            <FormattedMessage {...messages.customer.title} />
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.customer.name} />
            </div>
            <div>
              {reservation.customer.lastName}, {reservation.customer.name}
            </div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.customer.email} />
            </div>
            <div>{reservation.customer.email}</div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.customer.country} />
            </div>
            <div>
              <CountryRender
                countryCode={reservation.customer.country}
                locale={reservation.language}
              />
            </div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.customer.phone} />
            </div>
            <div>{reservation.customer.phone}</div>
          </div>
          <div className="mb-5">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.customer.comments} />
            </div>
            <div className="whitespace-pre-line">{roomReservation.comments || "-"}</div>
          </div>
        </div>

        <div>
          <div className="border-bottom pb-2 mb-3 text-[21px]">
            <FormattedMessage {...messages.dates.title} />
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.dates.checkIn} />
            </div>
            <div>
              <DateRender date={roomReservation.checkIn} format={"dd-MM-yyyy, E"} />
            </div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.dates.checkOut} />
            </div>
            <div>
              <DateRender date={roomReservation.checkOut} format={"dd-MM-yyyy, E"} />
            </div>
          </div>
          <div className="mb-5">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.dates.nights} />
            </div>
            <div>{roomReservation!.nights}</div>
          </div>
        </div>

        <div>
          <div className="border-bottom pb-2 mb-3 text-[21px]">
            <FormattedMessage {...messages.accommodation.title} />
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.accommodation.type} />
            </div>
            <div>
              <InventoryRender {...roomReservation} />
            </div>
            {roomReservation?.bookingOption?.combined ? (
              <div
                className="text-[#5CA8F4] underline mt-1 cursor-pointer"
                onClick={handleToggleCombinedBookingModal}>
                <FormattedMessage {...messages.accommodation.combinedRate} />
              </div>
            ) : null}
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.accommodation.totalPerPerson} />
            </div>
            <div>{roomReservation.capacity}</div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.accommodation.pricePerNight} /> (
              {roomReservation.totalAmount.currency})
            </div>
            <div>
              <DailyPricesRender {...roomReservation.dailyValues} />
            </div>
          </div>
          <div className="mb-5">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.accommodation.total} /> (
              {roomReservation.totalAmount.currency})
            </div>
            <div>
              <AmountRender amount={roomReservation.subTotalAmount.amount} />
            </div>
          </div>
        </div>

        {roomReservation.discounts && roomReservation.discounts.length > 0 && (
          <div>
            <div className="border-bottom pb-2 mb-3 text-[21px]">
              <FormattedMessage {...messages.discounts.title} />
            </div>
            {roomReservation.discounts.map((it, index) => (
              <div
                key={index}
                className={`mb-${
                  index === roomReservation?.discounts.length - 1 &&
                  !roomReservation?.bookingOption?.combined
                    ? "5"
                    : "3"
                }`}>
                <div className="font-weight-bold">{it.name}</div>
                <div>
                  <DiscountRender amount={roomReservation?.subTotalAmount} discount={it} />
                </div>
              </div>
            ))}
            <div
              className="text-[#5CA8F4] underline mb-5 cursor-pointer"
              onClick={handleToggleDiscountModal}>
              <FormattedMessage {...messages.discounts.seeAppliedDiscounts} />
            </div>
          </div>
        )}

        {roomReservation.selectedServices && roomReservation.selectedServices.length > 0 && (
          <div>
            <div className="border-bottom pb-2 mb-3 text-[21px]">
              <FormattedMessage {...messages.extras.title} />
            </div>
            {roomReservation.selectedServices.map((it, index) => (
              <div
                key={index}
                className={`mb-${
                  index === roomReservation?.selectedServices.length - 1 ? "5" : "3"
                }`}>
                <div className="font-weight-bold">{it.name}</div>
                <div>
                  <ExtraRender {...it} />
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <div className="border-bottom pb-2 mb-3 text-[21px]">
            <FormattedMessage {...messages.total.title} />
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.total.title} /> (<FormattedMessage {...totalMessage} />
              ) ({roomReservation.totalAmount.currency})
            </div>
            <div>{Number(roomReservation.totalAmount.amount).toFixed(2)}</div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.total.prePaid} /> (
              {roomReservation.totalAmount.currency})
            </div>
            <div>
              <AmountRender amount={roomReservation.guaranteeAmount.amount} />
            </div>
          </div>
          <div className="mb-3">
            <div className="font-weight-bold">
              <FormattedMessage {...messages.total.pending} /> (
              {roomReservation.totalAmount.currency})
            </div>
            <div>
              <AmountRender amount={pendingAmount} />
            </div>
          </div>

          {roomReservation.paymentDcc && (
            <>
              <div className="mb-3">
                <div className="font-weight-bold">
                  <FormattedMessage {...messages.total.prePaid} /> (
                  {roomReservation.paymentDcc.currencyCode})
                </div>
                <div>
                  <AmountRender
                    amount={roomReservation.paymentDcc.guaranteeAmount}
                  />
                </div>
              </div>
              <div className="mb-3">
                <div className="font-weight-bold">
                  <FormattedMessage {...messages.total.dccExchangeRateType} />
                </div>
                <div>
                  <ExchangeTypeRender
                    fromCurrency={roomReservation.paymentDcc.currencyCode}
                    toCurrency={roomReservation.totalAmount.currency}
                    amount={roomReservation.paymentDcc.exchangeRateWithMarkupFee}
                  />
                </div>
              </div>
              <div className="mb-3">
                <div className="font-weight-bold">
                  <FormattedMessage {...messages.total.dccMarkup} />
                </div>
                <div>{roomReservation.paymentDcc.markup}</div>
              </div>
            </>
          )}
        </div>

        <div className="mt-5">
          {showEditButton && (
            <Button className="btn btn-default mr-3" onClick={handleModification}>
              <FormattedMessage {...messages.actions.edit} />
            </Button>
          )}
          <Button className="btn btn-default mr-3" onClick={window.print}>
            <FormattedMessage {...messages.actions.print} />
          </Button>

          {showCCButton && (
            <CreditCardDataTooltip>
              <Button className="btn btn-default mr-3" onClick={onShowCreditCardData}>
                <FormattedMessage {...messages.actions.showCreditCardData} />
              </Button>
            </CreditCardDataTooltip>
          )}

          <Button className="btn btn-default" onClick={handleSeeConditions}>
            <FormattedMessage {...messages.actions.viewConditions} />
          </Button>
        </div>

        <div className="mt-5">
          <StyledGoBackButton className="btn btn-default" onClick={history.goBack}>
            <FormattedMessage {...messages.actions.back} />
          </StyledGoBackButton>
        </div>
      </Card.Body>
    </StyledCard>
  );
}
